/**
 * Typed wrapper around the pool contract. Mirrors the surface that
 * Acton's auto-generated wrappers would expose, but written manually here so
 * the CLI and relayer can depend on a stable interface even before the
 * contracts have been compiled. The real Acton wrappers will live under
 * `apps/contracts/wrappers/` and supersede this once they're generated.
 */
import { Address, beginCell, SendMode, toNano, } from "@ton/core";
import { OP_CODES } from "./constants";
export class TonadoPool {
    address;
    init;
    constructor(address, init) {
        this.address = address;
        this.init = init;
    }
    static at(address) {
        return new TonadoPool(address);
    }
    /**
     * Build a pool's initial data cell. Must match `PoolStorage` in
     * pool_native.tolk byte-for-byte. Used by deploy scripts and by sandbox
     * tests to materialize a contract instance.
     *
     * `currentRoot` MUST be the empty-tree root at MERKLE_TREE_HEIGHT — the
     * pool rejects any deposit whose `prevRoot` doesn't match. Use
     * `buildMerkleTree({ leaves: [] }).root` to compute it.
     *
     * Pass `owner: null` for a burn-key deploy. The cell will encode
     * [BURN_ADDRESS] (workchain=0, hash=0x00…00) — a valid addr_std with no
     * recoverable private key — so the admin handlers' `senderAddress ==
     * owner` check can never succeed. We use a sentinel addr_std rather than
     * addr_none because the Tolk `address` type rejects addr_none at parse
     * time (exit code 9 from every get-method), and switching the storage
     * struct to `any_address` would expand audit scope. See
     * docs/threat-model.md "Owner / governance model".
     */
    static buildInitialData(opts) {
        const ownerAddr = opts.owner ?? TonadoPool.BURN_ADDRESS;
        const treasuryAddr = opts.treasury ?? TonadoPool.BURN_ADDRESS;
        const revenueCell = beginCell()
            .storeAddress(treasuryAddr)
            .storeUint(opts.protocolFeeBps ?? 0, 16)
            .endCell();
        return beginCell()
            .storeUint(opts.paused ? 1 : 0, 1)
            .storeAddress(ownerAddr)
            .storeCoins(opts.denomination)
            .storeUint(0, 32) // nextIndex
            .storeUint(0, 32) // nullifiersCount
            .storeUint(opts.emptyTreeRoot, 256) // currentRoot = empty-tree root
            .storeUint(0, 1) // roots: cell? = null
            .storeUint(0, 1) // nullifiers: cell? = null
            .storeUint(0, 1) // commitments: cell? = null
            .storeRef(revenueCell) // RevenueConfig sub-ref
            .endCell();
    }
    /**
     * Canonical "burned" owner address: workchain=0, hash=0x00…00. A valid
     * addr_std with no recoverable private key. Used when
     * `buildInitialData({ owner: null })` is called to make the admin
     * handlers permanently uncallable on mainnet pools.
     */
    static BURN_ADDRESS = new Address(0, Buffer.alloc(32, 0));
    /** Construct a deployable instance from code + initial-state inputs.
     *  Pass `owner: null` for a burn-key deploy (admin handlers uncallable). */
    static fromInit(opts) {
        const data = TonadoPool.buildInitialData({
            owner: opts.owner,
            denomination: opts.denomination,
            emptyTreeRoot: opts.emptyTreeRoot,
            treasury: opts.treasury,
            protocolFeeBps: opts.protocolFeeBps,
        });
        // Address derivation: keccak-style hash of the state-init cell.
        const stateInitCell = beginCell()
            .storeUint(0, 2).storeUint(3, 2)
            .storeRef(opts.code).storeRef(data)
            .storeUint(0, 1)
            .endCell();
        const wc = opts.workchain ?? 0;
        const hash = stateInitCell.hash();
        return new TonadoPool(new Address(wc, hash), { code: opts.code, data });
    }
    /** ---- Send: deposit native TON ---- */
    async sendDepositNative(provider, via, opts) {
        const body = await buildDepositBody({
            queryId: opts.queryId ?? 0n,
            commitment: opts.commitment,
            prevRoot: opts.prevRoot,
            newRoot: opts.newRoot,
            leafIndex: opts.leafIndex,
            mergeProof: opts.mergeProof,
        });
        await provider.internal(via, {
            value: opts.value,
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    /** ---- Send: withdraw with Groth16 proof ---- */
    async sendWithdraw(provider, via, opts) {
        const body = await buildWithdrawBody({
            queryId: opts.queryId ?? 0n,
            proof: opts.proof,
            root: opts.root,
            nullifierHash: opts.nullifierHash,
            recipient: opts.recipient,
            relayer: opts.relayer,
            fee: opts.fee,
            refund: opts.refund,
        });
        await provider.internal(via, {
            value: opts.value ?? toNano("0.2"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    /** ---- Get methods (Tolk 1.0 camelCase) ---- */
    async getIsSpent(provider, nullifierHash) {
        const res = await provider.get("isSpent", [
            { type: "int", value: nullifierHash },
        ]);
        return res.stack.readNumber() !== 0;
    }
    /**
     * Check whether a given root is in the on-chain known-roots set. The Tolk
     * 1.0 rewrite replaced the ring-buffer history with a set-membership map,
     * so callers ask "is this root known?" rather than fetching a list.
     */
    async isKnownRoot(provider, root) {
        const res = await provider.get("isKnownRoot", [
            { type: "int", value: root },
        ]);
        return res.stack.readNumber() !== 0;
    }
    async getDenomination(provider) {
        const res = await provider.get("getDenomination", []);
        return res.stack.readBigNumber();
    }
    async getNextIndex(provider) {
        const res = await provider.get("getNextIndex", []);
        return res.stack.readNumber();
    }
    async isPaused(provider) {
        const res = await provider.get("isPaused", []);
        return res.stack.readNumber() !== 0;
    }
    // ---- Revenue config (deposit-time protocol fee) ----
    /** Owner-only. Set treasury address + protocol fee in bps. Capped at
     *  MAX_FEE_BPS (200 = 2%). */
    async sendAdminSetRevenueConfig(provider, via, opts) {
        const body = beginCell()
            .storeUint(0xded057f4, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeAddress(opts.treasury)
            .storeUint(opts.protocolFeeBps, 16)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async getTreasury(provider) {
        const res = await provider.get("getTreasury", []);
        return res.stack.readAddress();
    }
    async getProtocolFeeBps(provider) {
        const res = await provider.get("getProtocolFeeBps", []);
        return res.stack.readNumber();
    }
    async getMaxFeeBps(provider) {
        const res = await provider.get("getMaxFeeBps", []);
        return res.stack.readNumber();
    }
}
/**
 * Typed wrapper around the jetton pool contract (pool_jetton.tolk).
 *
 * Mirrors `TonadoPool` but adapted to the jetton storage layout (extra
 * `JettonConfig` sub-ref) and TEP-74 deposit flow. Withdraw shape is
 * IDENTICAL to native (the wire format is asset-agnostic), so callers
 * can reuse `buildWithdrawBody` for both.
 *
 * Deploy is two-pass:
 *   1. `fromInit({ owner, denomination, jettonMaster, ... })` computes
 *      the pool address from initial state with `poolJettonWallet =
 *      BURN_ADDRESS`.
 *   2. The deployer calls `jettonMaster.get_wallet_address(poolAddress)`
 *      off-chain to learn the canonical pool jetton wallet, then sends
 *      a one-shot AdminBindJettonWallet op. `sendAdminBindJettonWallet`
 *      builds that body.
 */
export class TonadoJettonPool {
    address;
    init;
    constructor(address, init) {
        this.address = address;
        this.init = init;
    }
    static at(address) {
        return new TonadoJettonPool(address);
    }
    /** Same burn sentinel as `TonadoPool.BURN_ADDRESS`. Used for the
     *  unbound poolJettonWallet slot before the AdminBindJettonWallet
     *  one-shot fires, and for credibly-neutral owner. */
    static BURN_ADDRESS = TonadoPool.BURN_ADDRESS;
    /**
     * Build a jetton pool's initial data cell. MUST match `PoolStorage`
     * in pool_jetton.tolk byte-for-byte.
     *
     * Storage layout (inline):
     *   bool        paused
     *   address     owner
     *   coins       denomination
     *   ref         config  → Cell<JettonConfig> { jettonMaster, poolJettonWallet,
     *                                              treasury, protocolFeeBps }
     *   uint32      nextIndex
     *   uint32      nullifiersCount
     *   uint256     currentRoot
     *   Maybe ref   roots
     *   Maybe ref   nullifiers
     *   Maybe ref   commitments
     *
     * Sub-ref config carries the jetton binding AND the revenue config
     * (packed together to stay within the cell's 4-ref budget).
     */
    static buildInitialData(opts) {
        const ownerAddr = opts.owner ?? TonadoJettonPool.BURN_ADDRESS;
        const poolWalletAddr = opts.poolJettonWallet ?? TonadoJettonPool.BURN_ADDRESS;
        const treasuryAddr = opts.treasury ?? TonadoJettonPool.BURN_ADDRESS;
        const config = beginCell()
            .storeAddress(opts.jettonMaster)
            .storeAddress(poolWalletAddr)
            .storeAddress(treasuryAddr)
            .storeUint(opts.protocolFeeBps ?? 0, 16)
            .endCell();
        return beginCell()
            .storeUint(opts.paused ? 1 : 0, 1)
            .storeAddress(ownerAddr)
            .storeCoins(opts.denomination)
            .storeRef(config)
            .storeUint(0, 32) // nextIndex
            .storeUint(0, 32) // nullifiersCount
            .storeUint(opts.emptyTreeRoot, 256) // currentRoot
            .storeUint(0, 1) // roots: cell? = null
            .storeUint(0, 1) // nullifiers: cell? = null
            .storeUint(0, 1) // commitments: cell? = null
            .endCell();
    }
    /** Compute the deployable contract address from code + init opts. */
    static fromInit(opts) {
        const data = TonadoJettonPool.buildInitialData({
            owner: opts.owner,
            denomination: opts.denomination,
            jettonMaster: opts.jettonMaster,
            emptyTreeRoot: opts.emptyTreeRoot,
            treasury: opts.treasury,
            protocolFeeBps: opts.protocolFeeBps,
        });
        const stateInitCell = beginCell()
            .storeUint(0, 2).storeUint(3, 2)
            .storeRef(opts.code).storeRef(data)
            .storeUint(0, 1)
            .endCell();
        const wc = opts.workchain ?? 0;
        const hash = stateInitCell.hash();
        return new TonadoJettonPool(new Address(wc, hash), { code: opts.code, data });
    }
    /** ---- Send: withdraw (same shape as native; wire format is
     *  asset-agnostic). The pool dispatches to its jetton fan-out path
     *  internally. ---- */
    async sendWithdraw(provider, via, opts) {
        const body = await buildWithdrawBody({
            queryId: opts.queryId ?? 0n,
            proof: opts.proof,
            root: opts.root,
            nullifierHash: opts.nullifierHash,
            recipient: opts.recipient,
            relayer: opts.relayer,
            fee: opts.fee,
            refund: opts.refund,
        });
        await provider.internal(via, {
            value: opts.value ?? toNano("0.3"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    /** ---- Send: admin-bind the pool's own jetton wallet. One-shot — the
     *  contract refuses to rebind once the wallet is bound. ---- */
    async sendAdminBindJettonWallet(provider, via, opts) {
        const body = beginCell()
            .storeUint(OP_CODES.ADMIN_BIND_JETTON_WALLET, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeAddress(opts.walletAddress)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    /** ---- Get methods (same names + return types as the native pool
     *  where applicable; jetton-specific ones below). ---- */
    async getIsSpent(provider, nullifierHash) {
        const res = await provider.get("isSpent", [
            { type: "int", value: nullifierHash },
        ]);
        return res.stack.readNumber() !== 0;
    }
    async isKnownRoot(provider, root) {
        const res = await provider.get("isKnownRoot", [
            { type: "int", value: root },
        ]);
        return res.stack.readNumber() !== 0;
    }
    async getDenomination(provider) {
        const res = await provider.get("getDenomination", []);
        return res.stack.readBigNumber();
    }
    async getNextIndex(provider) {
        const res = await provider.get("getNextIndex", []);
        return res.stack.readNumber();
    }
    async isPaused(provider) {
        const res = await provider.get("isPaused", []);
        return res.stack.readNumber() !== 0;
    }
    /** The TEP-74 master this pool tracks. */
    async getJettonMaster(provider) {
        const res = await provider.get("getJettonMaster", []);
        return res.stack.readAddress();
    }
    /** The pool's own jetton wallet. Returns BURN_ADDRESS if not yet bound. */
    async getPoolJettonWallet(provider) {
        const res = await provider.get("getPoolJettonWallet", []);
        return res.stack.readAddress();
    }
    // ---- Revenue config ----
    /** Owner-only. Set treasury address + protocol fee in bps. */
    async sendAdminSetRevenueConfig(provider, via, opts) {
        const body = beginCell()
            .storeUint(0xded057f6, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeAddress(opts.treasury)
            .storeUint(opts.protocolFeeBps, 16)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async getTreasury(provider) {
        const res = await provider.get("getTreasury", []);
        return res.stack.readAddress();
    }
    async getProtocolFeeBps(provider) {
        const res = await provider.get("getProtocolFeeBps", []);
        return res.stack.readNumber();
    }
    async getMaxFeeBps(provider) {
        const res = await provider.get("getMaxFeeBps", []);
        return res.stack.readNumber();
    }
}
/**
 * Build a `DepositNative` message body matching `pool_native.tolk`.
 *
 * Layout:
 *   uint32  op = 0xded05701
 *   uint64  queryId
 *   uint256 commitment
 *   uint256 prevRoot
 *   uint256 newRoot
 *   uint32  leafIndex
 *   ref     mergeProof  → Cell<MergeProof> (3 sub-refs: piA, piB, piC compressed)
 *
 * Inline total: 32 + 64 + 256 + 256 + 256 + 32 = 896 bits — fits a single cell.
 */
export async function buildDepositBody(opts) {
    const proofCell = await serialiseGroth16Proof(opts.mergeProof);
    return beginCell()
        .storeUint(OP_CODES.DEPOSIT_NATIVE, 32)
        .storeUint(opts.queryId, 64)
        .storeUint(opts.commitment, 256)
        .storeUint(opts.prevRoot, 256)
        .storeUint(opts.newRoot, 256)
        .storeUint(opts.leafIndex, 32)
        .storeRef(proofCell)
        .endCell();
}
export async function buildWithdrawBody(opts) {
    // Layout matches pool_native.tolk :: Withdraw struct (Tolk 1.0 rewrite).
    //
    // Root cell:
    //   uint32  op = 0xded05702
    //   uint64  queryId
    //   uint256 root
    //   uint256 nullifierHash
    //   ref     coreFields  → Cell<WithdrawCoreFields>
    //   ref     proof       → Cell<WithdrawProof>  (3 sub-refs: piA, piB, piC)
    //
    // The address+coins set wouldn't fit inline with the two 256-bit hashes
    // (would overflow 1023 bits), so we pack them into a separate ref cell.
    const proofCell = await serialiseGroth16Proof(opts.proof);
    const coreCell = beginCell()
        .storeAddress(opts.recipient)
        .storeAddress(opts.relayer)
        .storeCoins(opts.fee)
        .storeCoins(opts.refund)
        .endCell();
    return beginCell()
        .storeUint(OP_CODES.WITHDRAW, 32)
        .storeUint(opts.queryId, 64)
        .storeUint(opts.root, 256)
        .storeUint(opts.nullifierHash, 256)
        .storeRef(coreCell)
        .storeRef(proofCell)
        .endCell();
}
/**
 * Serialise a Groth16-BLS12-381 proof into a TON cell, using ZCash/IRTF
 * compressed point format which is what the TVM `BLS_*` opcodes (and the
 * verifier exported by `export-ton-verifier`) consume.
 *
 *   pi_a: 48-byte compressed G1   (1 ref cell, 384 bits inline)
 *   pi_b: 96-byte compressed G2   (1 ref cell, 768 bits inline)
 *   pi_c: 48-byte compressed G1   (1 ref cell, 384 bits inline)
 *
 * We delegate the actual compression to `export-ton-verifier`'s
 * `groth16CompressProof` so the bit-fiddling stays in lockstep with the
 * verifier template that produced `verifier.tolk`.
 */
export async function serialiseGroth16Proof(proof) {
    // Local vendored implementation (browser-safe). The upstream
    // `export-ton-verifier` package brings in Node-only fastfile via its
    // template-generation surface, which broke Vite/Rollup browser bundling.
    // The compression math itself is pure ffjavascript ops — see
    // `./groth16Compress.ts`.
    const { groth16CompressProof } = await import("./groth16Compress");
    const { pi_a, pi_b, pi_c } = await groth16CompressProof(proof);
    // @ton/core 0.63's `storeBuffer` calls `src.copy(...)` (a Node Buffer
    // method) under the hood, so a plain Uint8Array doesn't satisfy it.
    // Convert when Buffer is available (Node + most browser bundles with
    // a Buffer polyfill); fall through for raw browser builds where the
    // caller is expected to have a polyfill in scope.
    const toBuf = (u) => {
        if (typeof Buffer !== "undefined") {
            return Buffer.from(u.buffer, u.byteOffset, u.byteLength);
        }
        return u;
    };
    const aCell = beginCell().storeBuffer(toBuf(pi_a)).endCell();
    const bCell = beginCell().storeBuffer(toBuf(pi_b)).endCell();
    const cCell = beginCell().storeBuffer(toBuf(pi_c)).endCell();
    return beginCell().storeRef(aCell).storeRef(bCell).storeRef(cCell).endCell();
}
//# sourceMappingURL=pool.js.map